import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, ShieldAlert, ShieldCheck, Globe } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MODO_TONO = {
  real: "border-emerald-300 bg-emerald-50 text-emerald-800",
  simulacion: "border-amber-300 bg-amber-50 text-amber-900",
  indeterminado: "border-red-300 bg-red-50 text-red-800",
};

function Dato({ label, children, testid }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <div className="mt-1 text-sm text-slate-800" data-testid={testid}>{children}</div>
    </div>
  );
}

export default function EstadoScreening() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get("/screening/estado-cotejo-externo").then((r) => r.data));
    } catch (e) {
      toast.error(apiError(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (user?.role !== "super_admin") {
    return (
      <div data-testid="estado-screening-sin-permiso" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        Panel de plataforma: solo el super administrador.
      </div>
    );
  }

  return (
    <div data-testid="estado-screening-page" className="space-y-6">
      <PageHeader title="Estado del screening"
        subtitle="Cómo está cotejando la plataforma AHORA MISMO: modo del cotejo externo —el único cotejo que corre en las altas—, estado del proveedor, listas del Centro que usa el barrido nocturno y último cotejo externo exitoso." />

      <div className="flex justify-end">
        <Button data-testid="estado-screening-recargar" variant="outline" size="sm" onClick={cargar} disabled={loading} className="gap-1.5">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Actualizar
        </Button>
      </div>

      {loading && !data ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Consultando el estado real del motor...
        </div>
      ) : !data ? (
        <p className="text-sm text-slate-500">Sin datos.</p>
      ) : (
        <>
          <div data-testid="estado-screening-modo"
               className={`flex items-start gap-3 rounded-lg border-l-4 p-4 ${MODO_TONO[data.modo_cotejo_externo] || MODO_TONO.indeterminado}`}>
            {data.consulta_real ? <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0" /> : <ShieldAlert className="mt-0.5 h-6 w-6 shrink-0" />}
            <div className="min-w-0">
              <p className="text-sm font-semibold" data-testid="estado-screening-modo-titulo">
                Cotejo externo: {data.modo_etiqueta}
              </p>
              <p className="mt-0.5 text-xs opacity-90">
                APP_ENV del proceso: <span className="font-mono">{data.app_env}</span>
                {data.override_preview ? " · override de consulta real ACTIVO" : ""}
                {" · proveedor en modo "}<span className="font-mono">{data.proveedor?.modo}</span>
                {data.proveedor?.activo ? " (activo)" : " (inactivo)"}
              </p>
              {!data.consulta_real && (
                <p className="mt-1 text-xs font-semibold">
                  Ningún alta está ejecutando el cotejo externo de verdad: cada registro queda
                  PENDIENTE y el resultado dice «cotejo externo NO realizado», nunca «Sin coincidencia».
                </p>
              )}
            </div>
          </div>

          {!!data.advertencias?.length && (
            <div data-testid="estado-screening-advertencias" className="rounded-lg border border-amber-300 bg-amber-50 p-4">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-900">
                <AlertTriangle className="h-4 w-4" /> Qué falta para que el cotejo sea confiable
              </p>
              <ul className="space-y-1.5 text-xs text-amber-900">
                {data.advertencias.map((a, i) => (
                  <li key={`adv-${i}`} data-testid={`estado-screening-advertencia-${i}`} className="flex gap-1.5">
                    <span>•</span><span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Dato label="fuente_global" testid="estado-screening-fuente">
              <span className="font-mono">{data.fuente_global}</span>
              {data.fuente_global_ok
                ? <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> correcta</span>
                : <span className="ml-2 text-xs font-semibold text-red-700">las altas no deben leer listas locales</span>}
            </Dato>
            <Dato label="Listas del Centro de Listas" testid="estado-screening-leidas">
              {data.listas_centro_ambito}
            </Dato>
            <Dato label="Lista de Vigilancia FELVORA" testid="estado-screening-vigilancia">
              {data.lista_vigilancia?.entradas} entrada(s) · v{data.lista_vigilancia?.version || "—"}
            </Dato>
            <Dato label="Último cotejo externo exitoso" testid="estado-screening-ultimo">
              {data.ultimo_cotejo_externo_exitoso
                ? `${data.ultimo_cotejo_externo_exitoso.fecha} · ${data.ultimo_cotejo_externo_exitoso.origen || "—"}`
                : "Nunca"}
            </Dato>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Dato label="Evidencias de cotejo externo en expedientes" testid="estado-screening-evidencias">
              {data.evidencias_externas?.total || 0} documento(s)
              {data.evidencias_externas?.simuladas_en_ultimas_500
                ? <span className="ml-2 font-semibold text-red-700">
                    · {data.evidencias_externas.simuladas_en_ultimas_500} SIMULADA(S) en las últimas 500
                  </span>
                : <span className="ml-2 text-xs text-slate-500">· sin simuladas detectadas</span>}
            </Dato>
            <Dato label="Proveedor externo" testid="estado-screening-proveedor">
              Modo <span className="font-mono">{data.proveedor?.modo}</span>
              {data.proveedor?.activo ? " · activo" : " · inactivo (no hay consulta posible)"}
            </Dato>
          </div>

          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
              <Globe className="h-4 w-4" /> Listas del Centro de Listas — se cotejan en el barrido nocturno (no en las altas)
            </p>
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
              <table className="w-full min-w-[560px] text-left text-sm" data-testid="estado-screening-tabla">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Lista</th>
                    <th className="px-3 py-2">Versión</th>
                    <th className="px-3 py-2">Carga</th>
                    <th className="px-3 py-2 text-right">Entradas</th>
                    <th className="px-3 py-2">Origen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.listas_globales?.map((l, i) => (
                    <tr key={`lg-${i}`} data-testid={`estado-screening-lista-${i}`} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium text-slate-800">{l.nombre}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">{l.version || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{l.fecha_carga || "—"}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${l.entradas ? "text-slate-800" : "text-red-700"}`}>
                        {l.entradas}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {l.demo
                          ? <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 font-semibold text-amber-800">semilla demo</span>
                          : <span className="text-slate-500">carga oficial</span>}
                      </td>
                    </tr>
                  ))}
                  {!data.listas_globales?.length && (
                    <tr><td colSpan={5} className="px-3 py-4 text-center text-sm text-red-700">
                      No hay ninguna lista vigente en el Centro: el barrido nocturno no puede detectar a nadie.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-[11px] text-slate-400" data-testid="estado-screening-pendientes">
            Registros pendientes de cotejo externo: {data.pendientes?.prospectos} prospecto(s) y{" "}
            {data.pendientes?.clientes} cliente(s) · consultado el {data.consultado_en?.slice(0, 19).replace("T", " ")}
          </p>
        </>
      )}
    </div>
  );
}
